/**
 * Máy chủ giả của Trung tâm thông báo (ADR-0102). Giữ trạng thái trong bộ nhớ, tách theo phạm vi: cấu hình của tenant
 * (token tenant) và của chính nền tảng (token `PLATFORM_ADMIN`). Từ chối như notification-service: `412` khi
 * `If-Match` lệch, `422 NOTIFY_CHANNEL_LIMIT_REACHED` quá 20 cấu hình, `403 NOTIFY_NOT_ENTITLED` khi loại đang tắt,
 * gửi lại chỉ lần gửi đã dừng hẳn.
 *
 * Màu trạng thái không quyết ở đây (CLAUDE.md): bảng tông nằm ở `modules/canh-bao/constants.ts`.
 */
import type { ApiError } from '../lib/errors';
import type {
  MaKenh,
  NotifyAlarmTask,
  NotifyAlarmTaskWrite,
  NotifyAlarmType,
  NotifyCatalog,
  NotifyCatalogPatch,
  NotifyChannel,
  NotifyChannelCreate,
  NotifyChannelType,
  NotifyChannelUpdate,
  NotifyDelivery,
  NotifyField,
  NotifyOverview,
  NotifyRecord,
  NotifyRecordDetail,
  NotifyTestResult,
  NotifyUsage,
  LocBanGhi,
  PhamViCanhBao,
  TelegramChat,
  Trang,
} from '../modules/canh-bao/types';

const GOI_TAT_CA = ['TRIAL', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'] as const;
const TOI_DA_KENH = 20;

const f = (key: string, kind: NotifyField['kind'], extra: Partial<NotifyField> = {}): NotifyField => ({
  key,
  kind,
  required: false,
  secret: kind === 'SECRET',
  ...extra,
});

const LOAI_KENH: NotifyChannelType[] = [
  {
    code: 'TELEGRAM',
    enabled: true,
    plans: [...GOI_TAT_CA],
    adapter_version: '1.0.0',
    fields: [f('chat_id', 'TEXT', { required: true }), f('bot_token', 'SECRET', { required: true })],
  },
  {
    code: 'EMAIL',
    enabled: true,
    plans: [...GOI_TAT_CA],
    adapter_version: '1.0.0',
    fields: [
      f('use_platform_smtp', 'BOOLEAN', { default_value: false }),
      f('smtp_host', 'TEXT', { required: true }),
      f('smtp_port', 'NUMBER', {
        required: true,
        default_value: 465,
        min: 1,
        max: 65535,
      }),
      f('sender', 'TEXT', { required: true }),
      f('smtp_username', 'TEXT'),
      f('smtp_password', 'SECRET', { required: true }),
      f('recipients', 'EMAIL_LIST', { required: true }),
    ],
  },
  {
    code: 'WEBHOOK',
    enabled: true,
    plans: ['STANDARD', 'PROFESSIONAL', 'ENTERPRISE'],
    adapter_version: '1.0.0',
    fields: [
      f('url', 'SECRET', { required: true }),
      f('method', 'SELECT', {
        required: true,
        options: ['POST', 'PUT'],
        default_value: 'POST',
      }),
      f('content_type', 'SELECT', {
        required: true,
        options: ['application/json', 'application/x-www-form-urlencoded'],
        default_value: 'application/json',
      }),
      f('body_template', 'JSON'),
      f('headers', 'SECRET'),
      f('signing_secret', 'SECRET'),
    ],
  },
  {
    code: 'DISCORD',
    enabled: true,
    plans: [...GOI_TAT_CA],
    adapter_version: '1.0.0',
    fields: [f('webhook_url', 'SECRET', { required: true })],
  },
  {
    code: 'DINGTALK',
    enabled: true,
    plans: [...GOI_TAT_CA],
    adapter_version: '1.0.0',
    fields: [f('webhook_url', 'SECRET', { required: true }), f('signing_secret', 'SECRET')],
  },
  {
    code: 'WECOM',
    enabled: false,
    plans: [...GOI_TAT_CA],
    adapter_version: '1.0.0',
    fields: [f('webhook_url', 'SECRET', { required: true })],
  },
  {
    code: 'FEISHU',
    enabled: true,
    plans: [...GOI_TAT_CA],
    adapter_version: '1.0.0',
    fields: [f('webhook_url', 'SECRET', { required: true }), f('signing_secret', 'SECRET')],
  },
];

const so = (key: string, mac: number, min = 1, max = 100000) =>
  f(key, 'NUMBER', { required: true, default_value: mac, min, max });

const loai = (
  code: string,
  scope: NotifyAlarmType['scope'],
  trigger: NotifyAlarmType['trigger'],
  params: NotifyField[] = [],
  extra: Partial<NotifyAlarmType> = {},
): NotifyAlarmType => ({
  code,
  scope,
  trigger,
  enabled: true,
  available: true,
  default_severity: 'WARNING',
  params,
  check_interval_seconds: trigger === 'SCHEDULED' ? 3600 : null,
  plans: [...GOI_TAT_CA],
  ...extra,
});

const MUC = f('min_severity', 'SELECT', {
  required: true,
  options: ['INFO', 'WARNING', 'CRITICAL'],
  default_value: 'WARNING',
});

const LOAI_CANH_BAO: NotifyAlarmType[] = [
  loai('subscription.expiring', 'TENANT', 'SCHEDULED', [so('days_before', 7, 1, 90)]),
  loai('license.expiring', 'TENANT', 'SCHEDULED', [so('days_before', 30, 1, 180)]),
  loai('quota.ai_tokens', 'TENANT', 'EVENT', [
    f('level', 'SELECT', {
      required: true,
      options: ['80', '100'],
      default_value: '80',
    }),
  ]),
  loai('quota.users', 'TENANT', 'SCHEDULED', [so('threshold_percent', 80, 1, 100)], { available: false }),
  loai('quota.storage', 'TENANT', 'SCHEDULED', [so('threshold_percent', 80, 1, 100)], { available: false }),
  loai('quota.notifications', 'TENANT', 'SCHEDULED', [so('threshold_percent', 80, 1, 100)], {
    check_interval_seconds: 300,
  }),
  loai('security.login_failed', 'TENANT', 'EVENT', [so('count', 5, 1, 1000), so('window_minutes', 10, 1, 60)], {
    default_severity: 'CRITICAL',
  }),
  loai('security.user_locked', 'TENANT', 'EVENT', [], {
    default_severity: 'CRITICAL',
  }),
  loai('security.admin_login', 'TENANT', 'EVENT', [], {
    available: false,
    default_severity: 'INFO',
  }),
  loai('security.alert', 'TENANT', 'EVENT', [MUC]),
  loai('security.ip_blocked', 'TENANT', 'EVENT', [], { available: false }),
  loai('domain.verification_lost', 'TENANT', 'EVENT', [], { available: false }),
  loai('module.unavailable', 'TENANT', 'EVENT', [], {
    available: false,
    default_severity: 'CRITICAL',
  }),
  loai('module.event', 'TENANT', 'EVENT', [], {
    available: false,
    default_severity: 'INFO',
  }),
  loai('notify.channel_failed', 'TENANT', 'EVENT'),
  loai(
    'infra.metric',
    'PLATFORM',
    'SCHEDULED',
    [
      f('metric', 'SELECT', {
        required: true,
        options: ['CPU', 'MEMORY', 'DISK', 'LOAD'],
        default_value: 'DISK',
      }),
      so('threshold_percent', 90, 1, 100),
      so('duration_minutes', 5, 1, 120),
    ],
    { available: false, check_interval_seconds: 60 },
  ),
  loai('infra.alertmanager', 'PLATFORM', 'EVENT', [MUC]),
  loai('platform.service_down', 'PLATFORM', 'SCHEDULED', [], {
    default_severity: 'CRITICAL',
    check_interval_seconds: 60,
  }),
  loai('platform.kafka_lag', 'PLATFORM', 'SCHEDULED', [so('lag_threshold', 1000, 1, 10000000)], {
    check_interval_seconds: 60,
  }),
  loai('platform.dead_letter', 'PLATFORM', 'SCHEDULED', [so('count', 10, 1, 100000)], { check_interval_seconds: 300 }),
  loai('platform.security_critical', 'PLATFORM', 'EVENT', [], {
    default_severity: 'CRITICAL',
  }),
  loai('platform.ops_warning', 'PLATFORM', 'EVENT'),
  loai('platform.license_expiring', 'PLATFORM', 'EVENT'),
  loai('platform.ai_token_request', 'PLATFORM', 'EVENT', [], {
    default_severity: 'INFO',
  }),
  loai('platform.tenant_over_quota', 'PLATFORM', 'SCHEDULED'),
];

// ───────────────────────── Trạng thái theo phạm vi ─────────────────────────

interface Kho {
  kenh: NotifyChannel[];
  tacVu: NotifyAlarmTask[];
  banGhi: NotifyRecordDetail[];
}

const gio = (truoc: number) => new Date(Date.now() - truoc * 60_000).toISOString();

function kenh(
  id: string,
  channel_type: MaKenh,
  name: string,
  config: Record<string, unknown>,
  secrets: string[],
  extra: Partial<NotifyChannel> = {},
): NotifyChannel {
  return {
    id,
    channel_type,
    name,
    config,
    secrets_set: secrets,
    enabled: true,
    disabled_reason: null,
    last_test_at: gio(60 * 20),
    last_test_ok: true,
    last_test_error: null,
    version: 1,
    created_at: gio(60 * 24 * 12),
    updated_at: gio(60 * 24 * 2),
    ...extra,
  };
}

function tacVu(
  id: string,
  type_code: string,
  title: string,
  params: Record<string, unknown>,
  channel_ids: string[],
  extra: Partial<NotifyAlarmTask> = {},
): NotifyAlarmTask {
  return {
    id,
    type_code,
    title,
    params,
    channel_ids,
    daily_limit: 3,
    total_limit: null,
    min_interval_sec: 600,
    send_from: '00:00',
    send_to: '23:59',
    enabled: true,
    last_check_at: gio(12),
    last_sent_at: gio(60 * 5),
    total_sent: 4,
    version: 1,
    created_at: gio(60 * 24 * 10),
    updated_at: gio(60 * 24 * 3),
    ...extra,
  };
}

function guiThanhCong(id: string, record_id: string, k: NotifyChannel, phut: number): NotifyDelivery {
  return {
    id,
    record_id,
    channel_id: k.id,
    channel_type: k.channel_type,
    channel_name: k.name,
    status: 'SUCCEEDED',
    attempts: 1,
    created_at: gio(phut),
    completed_at: gio(phut),
    attempt_log: [
      {
        attempt_no: 1,
        started_at: gio(phut),
        latency_ms: 412,
        outcome: 'SUCCEEDED',
        http_status: 200,
      },
    ],
  };
}

function khoTenant(): Kho {
  const tg = kenh('c1000000-0000-4000-8000-000000000001', 'TELEGRAM', 'Nhóm vận hành', { chat_id: '-1002233445566' }, [
    'bot_token',
  ]);
  const mail = kenh(
    'c1000000-0000-4000-8000-000000000002',
    'EMAIL',
    'Kế toán',
    {
      use_platform_smtp: false,
      smtp_host: 'smtp.hoabinh.vn',
      smtp_port: 465,
      sender: 'canh-bao@hoabinh.vn',
      smtp_username: 'canh-bao@hoabinh.vn',
      recipients: ['ketoan@hoabinh.vn', 'truongphong@hoabinh.vn', 'it@hoabinh.vn'],
    },
    ['smtp_password'],
  );
  const wh = kenh(
    'c1000000-0000-4000-8000-000000000003',
    'WEBHOOK',
    'Hệ thống ERP',
    {
      method: 'POST',
      content_type: 'application/json',
      body_template: '{"tieu_de": "{{title}}", "noi_dung": "{{message}}"}',
    },
    ['url', 'signing_secret'],
    {
      last_test_ok: false,
      last_test_error: 'Máy đích trả 401 Unauthorized.',
      enabled: false,
      disabled_reason: 'AUTO_FAILURES',
    },
  );
  const t1 = tacVu(
    'a1000000-0000-4000-8000-000000000001',
    'subscription.expiring',
    'Gói dịch vụ sắp hết hạn',
    { days_before: 7 },
    [mail.id, tg.id],
  );
  const t2 = tacVu(
    'a1000000-0000-4000-8000-000000000002',
    'security.login_failed',
    'Đăng nhập sai nhiều lần',
    { count: 5, window_minutes: 10 },
    [tg.id],
    {
      last_check_at: null,
      total_sent: 12,
      daily_limit: 5,
      min_interval_sec: 300,
    },
  );
  const t3 = tacVu(
    'a1000000-0000-4000-8000-000000000003',
    'quota.ai_tokens',
    'Token AI dùng 80%',
    { level: '80' },
    [mail.id, wh.id],
    {
      enabled: false,
      total_sent: 1,
      last_check_at: null,
    },
  );
  const r1 = 'b1000000-0000-4000-8000-000000000001';
  const r2 = 'b1000000-0000-4000-8000-000000000002';
  const r3 = 'b1000000-0000-4000-8000-000000000003';
  return {
    kenh: [tg, mail, wh],
    tacVu: [t1, t2, t3],
    banGhi: [
      {
        id: r1,
        tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000005',
        task_id: t2.id,
        task_title: t2.title,
        type_code: t2.type_code,
        severity: 'CRITICAL',
        title: 'Đăng nhập sai 7 lần trong 10 phút',
        body: 'Có 7 lần đăng nhập sai vào tổ chức Hoà Bình từ 3 địa chỉ IP trong 10 phút qua. Kiểm tra nhật ký đăng nhập.',
        status: 'SENT',
        suppressed_reason: null,
        deliveries_total: 1,
        deliveries_succeeded: 1,
        occurred_at: gio(35),
        created_at: gio(35),
        deliveries: [guiThanhCong('d1000000-0000-4000-8000-000000000001', r1, tg, 35)],
      },
      {
        id: r2,
        tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000005',
        task_id: t1.id,
        task_title: t1.title,
        type_code: t1.type_code,
        severity: 'WARNING',
        title: 'Gói dịch vụ còn 6 ngày',
        body: 'Gói Chuyên nghiệp của tổ chức hết hạn ngày 03/11/2026. Liên hệ FOXAI để gia hạn.',
        status: 'PARTIAL',
        suppressed_reason: null,
        deliveries_total: 2,
        deliveries_succeeded: 1,
        occurred_at: gio(60 * 5),
        created_at: gio(60 * 5),
        deliveries: [
          guiThanhCong('d1000000-0000-4000-8000-000000000002', r2, tg, 300),
          {
            id: 'd1000000-0000-4000-8000-000000000003',
            record_id: r2,
            channel_id: mail.id,
            channel_type: 'EMAIL',
            channel_name: mail.name,
            status: 'DEAD_LETTERED',
            attempts: 6,
            next_attempt_at: null,
            error_code: 'TIMEOUT',
            safe_error_message: 'Máy chủ SMTP không trả lời sau 15 giây.',
            created_at: gio(300),
            completed_at: gio(60),
            attempt_log: [1, 2, 3, 4, 5, 6].map((n) => ({
              attempt_no: n,
              started_at: gio(300 - n * 40),
              latency_ms: 15000,
              outcome: 'TRANSIENT_ERROR' as const,
              error_code: 'TIMEOUT',
              http_status: null,
            })),
          },
        ],
      },
      {
        id: r3,
        tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000005',
        task_id: t2.id,
        task_title: t2.title,
        type_code: t2.type_code,
        severity: 'CRITICAL',
        title: 'Đăng nhập sai 5 lần trong 10 phút',
        body: 'Có 5 lần đăng nhập sai vào tổ chức Hoà Bình trong 10 phút qua.',
        status: 'SUPPRESSED',
        suppressed_reason: 'MIN_INTERVAL',
        deliveries_total: 0,
        deliveries_succeeded: 0,
        occurred_at: gio(31),
        created_at: gio(31),
        deliveries: [],
      },
    ],
  };
}

function khoNenTang(): Kho {
  const tg = kenh(
    'c2000000-0000-4000-8000-000000000001',
    'TELEGRAM',
    'Trực vận hành FOXAI',
    { chat_id: '-1009988776655' },
    ['bot_token'],
  );
  const mail = kenh(
    'c2000000-0000-4000-8000-000000000002',
    'EMAIL',
    'Hộp thư vận hành',
    { use_platform_smtp: true, recipients: ['ops@foxai.vn'] },
    [],
  );
  const t1 = tacVu(
    'a2000000-0000-4000-8000-000000000001',
    'platform.service_down',
    'Service ngừng phản hồi',
    {},
    [tg.id, mail.id],
    {
      daily_limit: 20,
      min_interval_sec: 300,
      last_check_at: gio(1),
    },
  );
  const t2 = tacVu(
    'a2000000-0000-4000-8000-000000000002',
    'platform.kafka_lag',
    'Kafka lag cao',
    { lag_threshold: 5000 },
    [tg.id],
    {
      last_check_at: gio(1),
      total_sent: 0,
      last_sent_at: null,
    },
  );
  const r1 = 'b2000000-0000-4000-8000-000000000001';
  return {
    kenh: [tg, mail],
    tacVu: [t1, t2],
    banGhi: [
      {
        id: r1,
        tenant_id: null,
        task_id: t1.id,
        task_title: t1.title,
        type_code: t1.type_code,
        severity: 'CRITICAL',
        title: 'audit-service không sẵn sàng',
        body: '/ready của audit-service trả 503 trong 3 lần kiểm liên tiếp.',
        status: 'SENT',
        suppressed_reason: null,
        deliveries_total: 2,
        deliveries_succeeded: 2,
        occurred_at: gio(90),
        created_at: gio(90),
        deliveries: [
          guiThanhCong('d2000000-0000-4000-8000-000000000001', r1, tg, 90),
          guiThanhCong('d2000000-0000-4000-8000-000000000002', r1, mail, 90),
        ],
      },
    ],
  };
}

const KHO: Record<PhamViCanhBao, Kho> = {
  tenant: khoTenant(),
  'nen-tang': khoNenTang(),
};
let dem = 100;
const idMoi = (tien: string) => `${tien}000000-0000-4000-8000-${String((dem += 1)).padStart(12, '0')}`;
const SU_DUNG: NotifyUsage = { month: '2026-10-01', sent: 2380, limit: 10000 };

// ───────────────────────── Lỗi như notification-service ─────────────────────────

const loi = (code: string, status: number, message: string): ApiError => ({
  code,
  status,
  message,
});
const khongThay = () => loi('NOT_FOUND', 404, 'Không tìm thấy tài nguyên này. Có thể nó vừa bị gỡ ở phiên khác.');
const lechPhienBan = () =>
  loi('VERSION_MISMATCH', 412, 'Dữ liệu vừa được người khác sửa. Tải lại để xem bản mới nhất rồi sửa lại.');

function kiemPhienBan(version: number, ifMatch: string) {
  if (ifMatch !== `"${version}"`) throw lechPhienBan();
}

function loaiKenhDung(code: MaKenh) {
  const t = LOAI_KENH.find((x) => x.code === code);
  if (!t?.enabled) {
    throw loi('NOTIFY_NOT_ENTITLED', 403, 'Gói hiện tại chưa dùng được loại kênh này, hoặc nền tảng đang tắt nó.');
  }
}

function trang<T>(ds: T[]): Trang<T> {
  return { items: ds, next_cursor: null };
}

function boChiTiet(r: NotifyRecordDetail): NotifyRecord {
  const { deliveries: _bo, ...ban } = r;
  void _bo;
  return ban;
}

// ───────────────────────── API giả ─────────────────────────

export const mayChuCanhBao = {
  danhMuc(pv: PhamViCanhBao): NotifyCatalog {
    const scope = pv === 'tenant' ? 'TENANT' : 'PLATFORM';
    return {
      channel_types: LOAI_KENH.filter((t) => t.enabled),
      alarm_types: LOAI_CANH_BAO.filter((t) => t.scope === scope && t.enabled && t.available),
    };
  },

  danhMucNenTang(): NotifyCatalog {
    return {
      channel_types: LOAI_KENH.map((t) => ({ ...t })),
      alarm_types: LOAI_CANH_BAO.map((t) => ({ ...t })),
    };
  },

  suaLoaiKenh(code: MaKenh, du: NotifyCatalogPatch): NotifyChannelType {
    const t = LOAI_KENH.find((x) => x.code === code);
    if (!t) throw khongThay();
    if (du.enabled !== undefined) t.enabled = du.enabled;
    if (du.plans) t.plans = [...du.plans];
    return { ...t };
  },

  suaLoaiCanhBao(code: string, du: NotifyCatalogPatch): NotifyAlarmType {
    const t = LOAI_CANH_BAO.find((x) => x.code === code);
    if (!t) throw khongThay();
    if (du.enabled !== undefined) t.enabled = du.enabled;
    if (du.plans) t.plans = [...du.plans];
    return { ...t };
  },

  danhSachKenh(pv: PhamViCanhBao): Trang<NotifyChannel> {
    return trang(KHO[pv].kenh.map((k) => ({ ...k })));
  },

  taoKenh(pv: PhamViCanhBao, du: NotifyChannelCreate): NotifyChannel {
    loaiKenhDung(du.channel_type);
    const kho = KHO[pv];
    if (kho.kenh.length >= TOI_DA_KENH) {
      throw loi(
        'NOTIFY_CHANNEL_LIMIT_REACHED',
        422,
        'Đã đủ 20 cấu hình kênh nhận tin. Gỡ một cấu hình không còn dùng rồi thêm lại.',
      );
    }
    const loaiKenh = LOAI_KENH.find((x) => x.code === du.channel_type)!;
    const secrets = loaiKenh.fields.filter((x) => x.secret && du.config[x.key] !== undefined).map((x) => x.key);
    const config = Object.fromEntries(Object.entries(du.config).filter(([k]) => !secrets.includes(k)));
    const moi = kenh(idMoi('c9'), du.channel_type, du.name, config, secrets, {
      enabled: du.enabled ?? true,
      last_test_at: null,
      last_test_ok: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
    kho.kenh.push(moi);
    return { ...moi };
  },

  suaKenh(pv: PhamViCanhBao, id: string, ifMatch: string, du: NotifyChannelUpdate): NotifyChannel {
    const k = KHO[pv].kenh.find((x) => x.id === id);
    if (!k) throw khongThay();
    kiemPhienBan(k.version, ifMatch);
    const loaiKenh = LOAI_KENH.find((x) => x.code === k.channel_type)!;
    const secretMoi = loaiKenh.fields.filter((x) => x.secret && du.config[x.key] !== undefined).map((x) => x.key);
    k.name = du.name;
    k.config = Object.fromEntries(Object.entries(du.config).filter(([key]) => !secretMoi.includes(key)));
    k.secrets_set = Array.from(new Set([...k.secrets_set, ...secretMoi]));
    if (du.enabled !== undefined) {
      k.enabled = du.enabled;
      if (du.enabled) k.disabled_reason = null;
    }
    k.version += 1;
    k.updated_at = new Date().toISOString();
    return { ...k };
  },

  xoaKenh(pv: PhamViCanhBao, id: string): void {
    const kho = KHO[pv];
    if (!kho.kenh.some((x) => x.id === id)) throw khongThay();
    kho.kenh = kho.kenh.filter((x) => x.id !== id);
    kho.tacVu.forEach((t) => {
      t.channel_ids = t.channel_ids.filter((c) => c !== id);
    });
  },

  guiThu(pv: PhamViCanhBao, id: string): NotifyTestResult {
    const k = KHO[pv].kenh.find((x) => x.id === id);
    if (!k) throw khongThay();
    const ok = k.channel_type !== 'WEBHOOK';
    k.last_test_at = new Date().toISOString();
    k.last_test_ok = ok;
    k.last_test_error = ok ? null : 'Máy đích trả 401 Unauthorized.';
    return ok
      ? { ok: true }
      : {
          ok: false,
          error_code: 'AUTH_FAILED',
          message: 'Máy đích trả 401 Unauthorized.',
        };
  },

  chatTelegram(): { items: TelegramChat[] } {
    return {
      items: [
        {
          id: '-1002233445566',
          type: 'supergroup',
          title: 'Vận hành Hoà Bình',
        },
        { id: '581234567', type: 'private', title: 'Nguyễn Minh Anh' },
      ],
    };
  },

  danhSachTacVu(pv: PhamViCanhBao): Trang<NotifyAlarmTask> {
    return trang(KHO[pv].tacVu.map((t) => ({ ...t })));
  },

  taoTacVu(pv: PhamViCanhBao, du: NotifyAlarmTaskWrite): NotifyAlarmTask {
    const scope = pv === 'tenant' ? 'TENANT' : 'PLATFORM';
    const loaiCb = LOAI_CANH_BAO.find((x) => x.code === du.type_code);
    if (!loaiCb || loaiCb.scope !== scope || !loaiCb.enabled) {
      throw loi(
        'NOTIFY_NOT_ENTITLED',
        403,
        'Gói hiện tại chưa dùng được loại cảnh báo này, hoặc nền tảng đang tắt nó.',
      );
    }
    const moi = tacVu(idMoi('a9'), du.type_code, du.title, du.params ?? {}, du.channel_ids, {
      daily_limit: du.daily_limit ?? null,
      total_limit: du.total_limit ?? null,
      min_interval_sec: du.min_interval_sec ?? 600,
      send_from: du.send_from ?? '00:00',
      send_to: du.send_to ?? '23:59',
      enabled: du.enabled ?? true,
      last_check_at: null,
      last_sent_at: null,
      total_sent: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
    KHO[pv].tacVu.unshift(moi);
    return { ...moi };
  },

  suaTacVu(pv: PhamViCanhBao, id: string, ifMatch: string, du: NotifyAlarmTaskWrite): NotifyAlarmTask {
    const t = KHO[pv].tacVu.find((x) => x.id === id);
    if (!t) throw khongThay();
    kiemPhienBan(t.version, ifMatch);
    Object.assign(t, {
      title: du.title,
      params: du.params ?? {},
      channel_ids: du.channel_ids,
      daily_limit: du.daily_limit ?? null,
      total_limit: du.total_limit ?? null,
      min_interval_sec: du.min_interval_sec ?? t.min_interval_sec,
      send_from: du.send_from ?? t.send_from,
      send_to: du.send_to ?? t.send_to,
      enabled: du.enabled ?? t.enabled,
      version: t.version + 1,
      updated_at: new Date().toISOString(),
    });
    return { ...t };
  },

  xoaTacVu(pv: PhamViCanhBao, id: string): void {
    const kho = KHO[pv];
    if (!kho.tacVu.some((x) => x.id === id)) throw khongThay();
    kho.tacVu = kho.tacVu.filter((x) => x.id !== id);
  },

  danhSachBanGhi(pv: PhamViCanhBao, loc: LocBanGhi): Trang<NotifyRecord> {
    let ds = pv === 'tenant' ? KHO.tenant.banGhi : [...KHO['nen-tang'].banGhi, ...KHO.tenant.banGhi];
    if (loc.task_id) ds = ds.filter((r) => r.task_id === loc.task_id);
    if (loc.status) ds = ds.filter((r) => r.status === loc.status);
    if (pv === 'nen-tang' && loc.platform_only) ds = ds.filter((r) => !r.tenant_id);
    else if (pv === 'nen-tang' && loc.tenant_id) ds = ds.filter((r) => r.tenant_id === loc.tenant_id);
    return trang([...ds].sort((a, b) => b.created_at.localeCompare(a.created_at)).map(boChiTiet));
  },

  chiTietBanGhi(pv: PhamViCanhBao, id: string): NotifyRecordDetail {
    const ds = pv === 'tenant' ? KHO.tenant.banGhi : [...KHO['nen-tang'].banGhi, ...KHO.tenant.banGhi];
    const r = ds.find((x) => x.id === id);
    if (!r) throw khongThay();
    return structuredClone(r);
  },

  guiLai(pv: PhamViCanhBao, deliveryId: string): NotifyDelivery {
    const ds = pv === 'tenant' ? KHO.tenant.banGhi : [...KHO['nen-tang'].banGhi, ...KHO.tenant.banGhi];
    for (const r of ds) {
      const d = r.deliveries.find((x) => x.id === deliveryId);
      if (!d) continue;
      if (!['FAILED_PERMANENT', 'DEAD_LETTERED', 'CANCELLED'].includes(d.status)) {
        throw loi('INVALID_ARGUMENT', 409, 'Lần gửi này chưa dừng hẳn nên chưa gửi lại được.');
      }
      const moi: NotifyDelivery = {
        ...d,
        id: idMoi('d9'),
        status: 'PENDING',
        attempts: 0,
        error_code: null,
        safe_error_message: null,
        replay_of: d.id,
        next_attempt_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        completed_at: null,
        attempt_log: [],
      };
      r.deliveries.push(moi);
      r.deliveries_total += 1;
      return { ...moi };
    }
    throw khongThay();
  },

  suDung(): NotifyUsage {
    return { ...SU_DUNG };
  },

  tongQuan(): NotifyOverview {
    return {
      records_24h: 148,
      sent_24h: 131,
      failed_24h: 4,
      suppressed_24h: 13,
      pending_deliveries: 2,
      dead_lettered_24h: 1,
      by_channel: [
        { channel_type: 'TELEGRAM', succeeded: 96, failed: 1 },
        { channel_type: 'EMAIL', succeeded: 58, failed: 3 },
        { channel_type: 'WEBHOOK', succeeded: 12, failed: 0 },
      ],
    };
  },
};
